import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { CourseId } from '@/openapi/enum';

import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyCalcMixin from '@/user/mixins/StudyCalcMixin';
import UserMixin from '@/user/mixins/UserMixin';
import WindowAdjustMixin from '@/user/mixins/WindowAdjustMixin';

import Header from '@/user/components/header/Header.vue';
import BackButton from '@/user/components/header/backButton/BackButton.vue';
import ClearButton from '@/user/components/header/clearButton/ClearButton.vue';
import ScoreButton from '@/user/components/header/scoreButton/ScoreButton.vue';
import Calc2Confirm from '@/user/pages/authorized/study/calc/calc2/confirm/Calc2Confirm.vue';
import Calc2Work from '@/user/pages/authorized/study/calc/calc2/work/Calc2Work.vue';
import RestErrorBar from '@/user/components/study/restErrorBar/RestErrorBar.vue';
import RestTimeBar from '@/user/components/study/restTimeBar/RestTimeBar.vue';
import RestQuestionCount from '@/user/components/study/restQuestionCount/RestQuestionCount.vue';
import ConfirmDialog from '@/user/components/dialog/confirm/ConfirmDialog.vue';
import StudyResultDialog from '@/user/components/study/dialog/result/StudyResultDialog.vue';
import UserWindowSizeUtility from '@/classes/common/UserWindowSizeUtility';



/** 認識モード（文字式・小数、穴埋め、長文字列） */
enum MATH_RECOG_MODE {
  M_DEFAULT,
  M_BLANK,
  M_LONG
}

/** 入力確認欄定義 */
type Confirm = {
  confirmIndex: number;
  answerIndex: number;
  input: string;
  vinculumFlg: boolean;
  numerFlg: boolean;
  denomFlg: boolean;
  rootFlg: boolean;
  bracketsFlg: boolean;
  upperFlg: boolean;
  position?: object;
};

/** 穴埋め見出し定義 */
type ConfirmBlank = {
  str: string;
  position?: object;
};

/** お手つき上限 */
const INCORRECT_LIMIT = 3;

/** 入力確認欄最大数 */
const MAX_CONFIRM_COUNT = 24;

/** 入力確認欄1行最大数 */
const MAX_CONFIRM_ROW_COUNT = 12;

/** 指数判定対象文字列 */
const UPPER_DECISION_STRING = '23456789';

/** 指数判定直前文字列 */
const UPPER_DECISION_BEFORE_STRING = 'abcxyz)';

export default mixins(RoutesMixin, StudyCalcMixin, UserMixin, WindowAdjustMixin).extend({

  name: 'Calc2',

  components: {
    Header,
    BackButton,
    ClearButton,
    ScoreButton,
    RestErrorBar,
    RestTimeBar,
    RestQuestionCount,
    ConfirmDialog,
    StudyResultDialog,
    Calc2Confirm,
    Calc2Work
  },

  data(): {
    recogMode: MATH_RECOG_MODE;
    visibleBackDialog: boolean;
    confirms: Confirm[];
    confirmBlanks: ConfirmBlank[];
    blankType: string;
    answerCount: number;
    focusIndex: number;
    confirmInnerWidth: number;
    confirmInnerHeight: number;
    clearSwitch: boolean; // workでwatchして全消去処理を行うためだけのトグルスイッチ
  } {
    return {
      recogMode: MATH_RECOG_MODE.M_DEFAULT,
      visibleBackDialog: false,
      confirms: [],
      confirmBlanks: [],
      blankType: '',
      answerCount: 1,
      focusIndex: 0,
      confirmInnerWidth: 0,
      confirmInnerHeight: 0,
      clearSwitch: true,
    };
  },

  computed: {
    hasEmpty(): boolean {
      return _.some(this.confirms, c => c.input.length === 0);
    },
  },

  async created(): Promise<void> {
    // font読み込み
    await this.loadFont();

    this.$_initialize();
  },

  destroyed(): void {
    this.clearRestTimeInterval();
    this.clearStudyVariable();
    this.clearStudyResult();
  },

  methods: {
    // WindowAdjustMixin上書き
    onChangeWindowSizeHandler(): void {
      this.$_setConfirms();
    },
    back(): void {
      this.visibleBackDialog = false;
      this.$router.push(this.routes.UnitSelect);
    },
    clear(): void {
      this.$_setConfirms();
      this.clearSwitch = !this.clearSwitch;
    },
    score(): void {
      // 問題読み込み前は何もしない
      if (!this.isQuestionLoadCompelete) return;

      if (this.question === null) throw new Error('no question'); // 問題がない場合はthrow

      // 採点処理
      let isCorrect = true;
      if (!this.isAlwaysCorrect) isCorrect = this.$_judge(); // 正解ユーザー以外は答え合わせ

      // 不正解回数の更新
      if (!this.isTutorial && !this.hasEmpty && !isCorrect) this.incrementIncorretCount();

      // 学習メモの更新
      if (!isCorrect) this.addStudyMemo(String(this.question.questionNo));

      // 学習結果指定、結果モーダルを開く
      this.judgeScoreResult(this.hasEmpty, isCorrect, INCORRECT_LIMIT);
    },
    input(char: string): void {
      // 入力文字格納
      const confirm = this.confirms[this.focusIndex];
      confirm.input = char;

      // 指数フラグを設定
      const isSameGroup = (c0: Confirm, c1: Confirm) => {
        return c0.rootFlg === c1.rootFlg
            && c0.denomFlg === c1.denomFlg
            && c0.numerFlg === c1.numerFlg
            && c0.answerIndex === c1.answerIndex;
      };
      const bc = this.confirms[this.focusIndex - 1];
      if (bc !== undefined) {
        confirm.upperFlg = !bc.upperFlg
                        && isSameGroup(confirm, bc)
                        && UPPER_DECISION_BEFORE_STRING.indexOf(bc.input) !== -1
                        && UPPER_DECISION_STRING.indexOf(confirm.input) !== -1;
      }
      const ac = this.confirms[this.focusIndex + 1];
      if (ac != undefined) {
        ac.upperFlg = !confirm.upperFlg
                   && isSameGroup(confirm, ac)
                   && UPPER_DECISION_BEFORE_STRING.indexOf(confirm.input) !== -1
                   && UPPER_DECISION_STRING.indexOf(ac.input) !== -1;
      }

      // 次の未入力欄へフォーカス
      this.$_setNextFocus();
    },
    focus(index: number): void {
      const target = this.confirms[index];
      if (target.input === '√' || target.input === '(' || target.input === ')' || target.bracketsFlg) return;

      this.focusIndex = index;
    },

    // StudyMixin上書き
    nextQuestion(): void {
      this.forwardQuestionIndex();
      this.$_setConfirms();
      this.startTimer();
    },

    // StudyMixinを上書き、学習終了後に学習続行用
    initializeCourseComponent(): void {
      this.initializeStudyVariable();
      this.$_initialize();
    },

    async $_initialize(): Promise<void> {
      // 学習進度読み込み
      await this.loadProgressesState(CourseId.calc);

      // 初期化
      this.initializeStudyVariable();
      this.initializeStudyRequest();
      this.recogMode         = MATH_RECOG_MODE.M_DEFAULT;
      this.visibleBackDialog = false;
      this.confirms          = [];
      this.focusIndex        = 0;
      // this.clearSwitch       = true; // 初期化しない

      // 回答可能回数
      this.setIncorretLimit(INCORRECT_LIMIT);

      // ステージ名設定
      const stageName = this.createStageName();
      this.setStageName(stageName);

      // 問題取得
      if (this.isJudgeUnit())
        await this.loadJudgeQuestions(); // 判定モード
      else
        await this.loadQuestions();      // 判定モード以外

      // 問題加工
      this.$_calcRight();       // 解答算出
      this.addQuestionImages(); // 画像URL設定

      // 問題開始
      this.$_startQuestion();
    },
    $_startQuestion(): void {
      this.initializeQuestionIndex();
      this.nextQuestion();
    },
    $_getRight(): string {
      const right = this.question!.right;
      return right.charAt(0) === '#' ? right.substring(1) : right; // 正解の先頭が「#」の場合は省く
    },
    $_getInput(): string[] {
      let ret = new Array(this.answerCount);
      ret.fill('');

      // 長文字列
      if (this.recogMode === MATH_RECOG_MODE.M_LONG) {
        let rootFlg = false;
        let tmpRet  = '';
        this.confirms.forEach(confirm => {
          const input = confirm.input;
          // ルート終了処理
          if (rootFlg && !confirm.rootFlg) {
            tmpRet += ' ';
            rootFlg = false;
          }
          // ルート開始処理
          if (input === '√') {
            tmpRet += input;
            rootFlg = true;
          // 累乗の場合
          } else if (confirm.upperFlg) {
            tmpRet += '^' + input;
          // 通常
          } else {
            tmpRet += input;
          }
        });

        // 末尾の半角スペースを省いて格納
        ret[0] = tmpRet.trim();

      // その他
      } else {
        const length = this.confirms.length;
        let   index  = 0;
        for (let ansIndex = 0; ansIndex < this.answerCount; ansIndex++) {
          let fracFlg = false;
          let rootFlg = false;
          let numerStr = '';
          let denomStr = '';
          while (index < length) {
            const confirm = this.confirms[index];

            // 複数解答インデックスが次のインデックスに変わっていたらbreak
            if (confirm.answerIndex !== ansIndex) break;

            let   tmpRet = '';
            const input  = confirm.input;

            // 分数開始（分母が先に来るので分母フラグで判定）
            if (!fracFlg && confirm.denomFlg) {
              fracFlg  = true;
              denomStr = '';
              numerStr = '';
            }
            // ルート終了処理
            if (rootFlg && !confirm.rootFlg) {
              tmpRet += ' ';
              rootFlg = false;
            }
            // ルート開始処理
            if (input === '√') {
              tmpRet += input;
              rootFlg = true;
            // 累乗の場合
            } else if (confirm.upperFlg) {
              tmpRet += '^' + input;
            // 通常
            } else {
              tmpRet += input;
            }

            // 分数処理
            if (fracFlg) {
              // 分母、分子に入力文字格納
              if      (confirm.denomFlg) denomStr += tmpRet;
              else if (confirm.numerFlg) numerStr += tmpRet;

              // 分数終わり
              const next = this.confirms[index + 1];
              if (confirm.numerFlg && (next === undefined || !next.numerFlg)) {
                // ルート終了処理をしておく
                if (confirm.rootFlg) {
                  numerStr += ' ';
                  rootFlg = false;
                }
                // まとめて返り値に追加
                ret[ansIndex] += '[' + numerStr + '/' + denomStr + ']';
                fracFlg = false;
              }

            // 分数以外
            } else {
              ret[ansIndex] += tmpRet;
            }

            index++;
          }

          // 分子、分母ともに空白の分数は省略する
          ret[ansIndex] = ret[ansIndex].replace(/\[\/\]/g, '');

          // 末尾の半角スペースを省く
          ret[ansIndex] = ret[ansIndex].trim();
        }
      }

      return ret;
    },
    $_setConfirms(): void {
      if (this.question === null) throw new Error('no question'); // 問題がない場合はthrow

      const question  = this.question.question;
      const right     = this.$_getRight();
      const blankType = this.question.blankType;

      // 認識モード設定
      if      (blankType.length > 0) this.recogMode = MATH_RECOG_MODE.M_BLANK;
      else if (this.$_isLong(right)) this.recogMode = MATH_RECOG_MODE.M_LONG;
      else                           this.recogMode = MATH_RECOG_MODE.M_DEFAULT;

      // 解答数
      if (this.recogMode === MATH_RECOG_MODE.M_BLANK) this.answerCount = blankType.length;
      else                                            this.answerCount = 1;

      // 解答欄初期化
      this.confirms      = [];
      this.confirmBlanks = [];

      // まだ解答が設定されていない場合はここで終了
      if (right.length === 0) return;

      // M_BLANKの場合はblankを取得しておく
      if (this.recogMode === MATH_RECOG_MODE.M_BLANK) {
        // 接続文字列
        let conn = blankType.indexOf('AB') !== -1 ? ':' : '=';

        // 表示文字列
        switch (blankType.length) {
          case 1:
            this.confirmBlanks = [{ str: blankType + conn }];
            break;
          case 2:
            if (blankType === 'xx')
              this.confirmBlanks = [
                { str: 'x' + conn },
                { str: ',' }
              ];
            else
              this.confirmBlanks = [
                { str: blankType.charAt(0) + conn },
                { str: blankType.charAt(1) + conn }
              ];
            break;
          case 3:
            this.confirmBlanks = [
              { str: blankType.charAt(0) + conn },
              { str: blankType.charAt(1) + conn },
              { str: blankType.charAt(2) + conn }
            ];
            break;
          default:
            break;
        }
      }

      // 解答取得
      const tmpRight = right.indexOf('|') === -1 ? right : right.split('|')[0]; // 別解は一番前を取得
      const rights   = tmpRight.indexOf(';') === -1 ? tmpRight.split('$') : tmpRight.split(';');

      // 解答を解析しながら表示設定を決めていく
      const confirmWidth     = UserWindowSizeUtility.calculateRatio(32);
      const numerPosY        = UserWindowSizeUtility.calculateRatio(2);
      const intPosY          = UserWindowSizeUtility.calculateRatio(20);
      const denomPosY        = UserWindowSizeUtility.calculateRatio(38);
      let   useSecondLine    = tmpRight.indexOf('/') !== -1;
      let   posX             = 0;
      let   posY             = 0;
      let   innerWidth       = 0;
      let   blankIndex       = 0;
      let   confirmIndex     = 0;
      let   answerIndex      = 0;
      let   ansNewLineIndex  = -1;
      let   rootFlg          = false;
      let   fractionFlg      = false;
      let   bracketsFlg      = false;

      // posYの初期値調整
      posY = useSecondLine ? intPosY : numerPosY;

      // 解答解析と表示設定
      rights.forEach(r => {
        // M_BLANKの場合はblankを設置
        if (this.recogMode === MATH_RECOG_MODE.M_BLANK) {
          this.confirmBlanks[blankIndex].position = this.$_getConfirmPosObject(posX, posY);
          posX += confirmWidth;
          blankIndex++;

        // M_LONGの場合は解答入力欄の改行位置を決めておく
        } else if (this.recogMode === MATH_RECOG_MODE.M_LONG) {
          // 残りのうちconfirm設定対象の文字列
          const targetStr = r.replace(/\^/g, '').replace(/ /g, '').replace(/&/g, '').replace(/%-/g, '-').replace(/%/g, '+');
          // 1行に収まらない場合のみチェック
          if (MAX_CONFIRM_ROW_COUNT < targetStr.length) {
            const firstLine  = targetStr.substring(0, MAX_CONFIRM_ROW_COUNT);
            const secondLine = targetStr.substring(MAX_CONFIRM_ROW_COUNT);

            // まずは括弧で改行できるかチェック
            const lastBracketsIndex = firstLine.lastIndexOf('(');
            if (lastBracketsIndex !== -1) {
              const tmpSecondLine = firstLine.substring(lastBracketsIndex) + secondLine;
              // 2行目が収まれば改行位置を設定
              if (tmpSecondLine.length <= MAX_CONFIRM_ROW_COUNT) ansNewLineIndex = lastBracketsIndex;
            }

            // 括弧で改行できない場合は「+」「-」「±」で改行できるかチェック
            if (ansNewLineIndex === -1) {
              // 2行目の先頭文字が「+」「-」「±」ならそこで改行
              if (secondLine.charAt(0) === '+' || secondLine.charAt(0) === '-' || secondLine.charAt(0) === '±') {
                ansNewLineIndex = MAX_CONFIRM_ROW_COUNT;

              // 2行目の先頭文字が「+」「-」「±」以外の場合
              } else {
                const lastPlusMinusIndex = Math.max(firstLine.lastIndexOf('+'), firstLine.lastIndexOf('-'), firstLine.lastIndexOf('±'));
                if (lastPlusMinusIndex !== -1) {
                  const tmpSecondLine = firstLine.substring(lastPlusMinusIndex) + secondLine;
                  // 2行目が収まれば改行位置を設定
                  if (tmpSecondLine.length <= MAX_CONFIRM_ROW_COUNT) ansNewLineIndex = lastPlusMinusIndex;
                }
              }
            }

            // どこも改行できない場合はthrow
            if (ansNewLineIndex === -1) throw new Error('master error: ILLEGAL_LINE_BREAK');
          }
        }

        // 文字毎の処理
        for (let i = 0; i < r.length; i++) {
          // 解答が長すぎる場合はthrow
          if (MAX_CONFIRM_COUNT - 1 < confirmIndex) throw new Error('master error: TOO_LONG_RIGHT');

          const char    = r.charAt(i);
          const confirm = {
            answerIndex:       -1,
            input:       '',
            vinculumFlg: false,
            numerFlg:    false,
            denomFlg:    false,
            rootFlg:     false,
            bracketsFlg: false,
            upperFlg:    false,
          } as Confirm;

          // 文字毎の特殊処理
          if (char === '#' || char === '&') {
            continue;
          } else if (char === '(' || char === ')') {
            confirm.input = char;
          } else if (char === '√') {
            confirm.input = char;
            rootFlg = true;
          } else if (char === ' ') {
            rootFlg = false;
            continue;
          } else if (char === '%') {
            rootFlg = false;
            if (r.charAt(i + 1) === '-') continue;
          } else if (char === '^') {
            if (r.charAt(i - 1) === ')') bracketsFlg = true;
            continue;
          } else if (char === '[') {
            // 分数終了がない場合はthrow
            if (r.indexOf(']', i + 1) === -1) throw new Error('master error: ILLEGAL_ANSWER_FRACTION');
            fractionFlg = true;
            i++; // 文字を進めて分数処理へ
          } else if (char === ']') {
            fractionFlg = false;
            continue;
          } else if (bracketsFlg) {
            confirm.bracketsFlg = true;
            confirm.input = char;
            confirm.upperFlg = true;
            bracketsFlg = false;
          }

          // 分数の場合はまとめて設置
          if (fractionFlg) {
            const fracEndIndex  = r.indexOf(']', i);
            const fracStr       = r.slice(i, fracEndIndex);
            const fracArray     = fracStr.split('/');
            const validNumerStr = fracArray[0].replace(/\^/g, '').replace(/ /g, '').replace(/&/g, '');
            const validDenomStr = fracArray[1].replace(/\^/g, '').replace(/ /g, '').replace(/&/g, '');
            const fracDiff      = validNumerStr.length - validDenomStr.length;

            // 分母->分子の順で設置
            for (let j = fracArray.length - 1; -1 < j; j--) {
              rootFlg = false;
              const isNumer  = j === 0;
              let   fracPosX = posX;
              const fracPosY = isNumer ? numerPosY : denomPosY;

              for (let k = 0; k < fracArray[j].length; k++) {
                const fracChar    = fracArray[j].charAt(k);
                const fracConfirm = {
                  answerIndex:       -1,
                  input:       '',
                  vinculumFlg: false,
                  numerFlg:    false,
                  denomFlg:    false,
                  rootFlg:     false,
                  bracketsFlg: false,
                } as Confirm;

                // 文字毎の特殊処理
                if (fracChar === '(' || fracChar === ')') {
                  fracConfirm.input = fracChar;
                } else if (fracChar === '√') {
                  rootFlg = true;
                  fracConfirm.input = fracChar;
                } else if (fracChar === ' ') {
                  rootFlg = false;
                  continue;
                } else if (char === '%') {
                  rootFlg = false;
                  if (r.charAt(i + 1) === '-') continue;
                } else if (fracChar === '^') {
                  // 直前の文字が)の場合には次のcomfirmの指数表示フラグを立てる
                  if (r.charAt(i - 1) === ')') bracketsFlg = true;
                  continue;
                } else if (bracketsFlg) {
                  fracConfirm.bracketsFlg = true;
                  fracConfirm.input = fracChar;
                  fracConfirm.upperFlg = true;
                  bracketsFlg = false;
                }

                // 1文字目の場合はマージンのチェック
                if (k === 0) {
                  if      (isNumer  && fracDiff < 0) fracPosX += -1 * fracDiff * confirmWidth / 2; // 分子、かつ分子の方が短い場合はマージンを追加
                  else if (!isNumer && fracDiff > 0) fracPosX += fracDiff * confirmWidth / 2;      // 分母、かつ分母の方が短い場合はマージンを追加
                }

                // confirmの設置
                fracConfirm.confirmIndex = confirmIndex;
                fracConfirm.answerIndex  = answerIndex;
                fracConfirm.vinculumFlg  = (isNumer && fracDiff > 0) || (!isNumer && fracDiff <= 0); // 長い方に線分表示を任せる
                fracConfirm.numerFlg     = isNumer;
                fracConfirm.denomFlg     = !isNumer;
                fracConfirm.rootFlg      = rootFlg;
                fracConfirm.position     = this.$_getConfirmPosObject(fracPosX, fracPosY);
                this.confirms.push(fracConfirm);

                // 次の処理用
                fracPosX += confirmWidth;
                confirmIndex++;
              }
            }

            // 文字位置調整
            i    += fracStr.length - 1;
            posX += fracDiff < 0 ? confirmWidth * validDenomStr.length : confirmWidth * validNumerStr.length;
            if (innerWidth < posX) innerWidth = posX;
            continue;
          }

          // M_LONGの場合は改行チェック
          if (this.recogMode === MATH_RECOG_MODE.M_LONG && -1 < ansNewLineIndex && confirmIndex === ansNewLineIndex) {
            posX = 0;
            posY = denomPosY;
            useSecondLine = true;
          }

          // confirmの設置
          confirm.confirmIndex = confirmIndex;
          confirm.answerIndex  = answerIndex;
          confirm.denomFlg     = false;
          confirm.numerFlg     = false;
          confirm.rootFlg      = rootFlg;
          confirm.position     = this.$_getConfirmPosObject(posX, posY);
          this.confirms.push(confirm);

          // 次の処理用
          posX += confirmWidth;
          if (innerWidth < posX) innerWidth = posX;
          confirmIndex++;
        }

        // 複数解答インデックスを進める
        answerIndex++;
      });

      // 解答エリアのサイズを設定
      if (innerWidth / confirmWidth > MAX_CONFIRM_ROW_COUNT) throw new Error('master error: TOO_LONG_RIGHT_IN_ROW'); // 想定外のX座標をとるときはthrow
      this.confirmInnerWidth  = innerWidth;
      this.confirmInnerHeight = useSecondLine ? UserWindowSizeUtility.calculateRatio(72) : UserWindowSizeUtility.calculateRatio(36);

      // デフォルトフォーカスの設定
      this.focusIndex = 0;
      if (question.length > 0) {
        for (let i = 0; i < this.confirms.length; i++) {
          this.focusIndex = i;
          if (this.confirms[i].input === '') break;
        }
      }
    },
    $_setNextFocus(): void {
      // 右隣への移動を試みる
      const nextConfirm = this.confirms[this.focusIndex + 1];
      if (nextConfirm !== undefined && nextConfirm.input === '') {
        this.focus(this.focusIndex + 1);
        return;
      }
      // 左隣への移動を試みる
      const pervConfirm = this.confirms[this.focusIndex - 1];
      if (pervConfirm !== undefined && pervConfirm.input === '') {
        this.focus(this.focusIndex - 1);
        return;
      }
      // 先頭から順に空欄への移動を試みる
      for (let i = 0; i < this.confirms.length; i++) {
        if (this.confirms[i].input === '') {
          this.focus(i);
          return;
        }
      }
    },
    $_judge(): boolean {
      const input = this.$_getInput();
      const right = this.$_getRight();

      // 正解が1つのとき
      if (input.length === 1) {
        // 解答が並べ替え可能な場合（因数分解）
        if (right.indexOf('&') !== -1) {
          const rights = right.split('&');
          // 入力文字列から、解答文字列を省いていく
          let str = input[0];
          for (let i = 0; i < rights.length; i++) {
            const startIndex = str.indexOf(rights[i]);
            if (startIndex === -1) return false;
            // 係数は先頭に限り有効
            if (i === 0 && rights[i].charAt(0) !== '(') {
              if (startIndex !== 0) return false;
            }
            str = str.substring(0, startIndex) + str.substring(startIndex + rights[i].length);
          }
          // 文字列が全て消えたら正解
          return (str.length === 0) ? true : false;
        }
        // 解答が並べ替え可能な場合（ルートを含んだ式）
        else if (right.indexOf('%') !== -1) {
          const rights = right.split('%');
          // 入力文字列から、解答文字列を省いていく
          let str = input[0];
          let numStr = '';
          for (let i = 0; i < rights.length; i++) {
            // 数字だけの項は最後に省くので保留しておく
            if (rights[i].indexOf('√') === -1) {
              numStr = rights[i];
              continue;
            }
            const startIndex = str.indexOf(rights[i]);
            if (startIndex === -1) return false;
            str = str.substring(0, startIndex) + str.substring(startIndex + rights[i].length);
          }
          // 数字の項を解答文字列から省く
          if (numStr.length > 0) {
            const startIndex = str.indexOf(numStr);
            if (startIndex === -1) return false;
            str = str.substring(0, startIndex) + str.substring(startIndex + numStr.length);
          }
          // 残った文字は「+」と半角スペースのはずなので消去する
          str = str.replace(/\+/g, '');
          str = str.replace(/ /g, '');
          // 文字列が全て消えたら正解
          return (str.length === 0) ? true : false;
        }
        // 別解がある場合
        else if (right.indexOf('|') !== -1) {
          // 別解がある場合は、正解が空欄不可
          if (input[0].length === 0) return false;
          const tmpRight = right + '|'; // 正解の最後に「|」を足す
          const str = input + '|';
          return (tmpRight.indexOf(str) !== -1) ? true : false;
        }
        // その他
        else {
          return right === input[0];
        }

      // 正解が2つ以上の時
      } else {
        // 順一致問題
        if (right.indexOf(';') !== -1) {
          let inputs = input[0];
          for (let i = 1; i < input.length; i++) inputs += ';' + input[i];
          return right === inputs;

        // 順不一致問題
        } else if (right.indexOf('$') !== -1) {
          let tmpRight = right + '$'; // 正解の最後に「$」を足す
          for (let i = 0; i < input.length; i++) {
            const str = input[i] + '$';
            const startIndex = tmpRight.indexOf(str);
            if (startIndex === -1) return false;
            tmpRight = tmpRight.substring(0, startIndex) + tmpRight.substring(startIndex + str.length);
          }
          // 文字列がすべて消えたら正解
          return tmpRight.length === 0;
        }
      }

      // よくわからない場合は不正解
      return false;
    },
    $_calcRight(): void {
      this.questions.forEach(q => {
        if (q.right.length > 0 || q.question.length === 0) return; // 回答がある、または問題がない場合は次へ
        q.right = this.$_calc(q.question);
      });
    },
    $_calc(question: string): string {
      // 先頭が記号の場合は省く
      if (question.search(/[^0-9]/) === 0) question = question.substring(1);

      // 記号までを取得
      const index0 = question.indexOf('+');
      const index1 = question.indexOf('-');
      const index2 = question.indexOf('*');
      const index3 = question.indexOf('/');
      const index4 = question.indexOf('=');
      let operation = '';
      let s1 = '';
      let s2 = '';
      if (index0 !== -1) {
        operation = '+';
        s1 = question.substring(0, index0);
        s2 = question.substring(index0 + 1, index4);
      } else if (index1 !== -1) {
        operation = '-';
        s1 = question.substring(0, index1);
        s2 = question.substring(index1 + 1, index4);
      } else if (index2 !== -1) {
        operation = '*';
        s1 = question.substring(0, index2);
        s2 = question.substring(index2 + 1, index4);
      } else if (index3 !== -1) {
        operation = '/';
        s1 = question.substring(0, index3);
        s2 = question.substring(index3 + 1, index4);
      }

      // 整数の場合
      if (s1.indexOf('.') === -1 && s2.indexOf('.') === -1) {
        const i1 = Number(s1);
        const i2 = Number(s2);
        if (operation === '+') return String(i1 + i2);
        if (operation === '-') return String(i1 - i2);
        if (operation === '*') return String(i1 * i2);
        if (operation === '/') {
          const r0 = Math.floor(i1 / i2);
          const r1 = i1 % i2;
          if (r1 === 0) return String(r0);
          else          return r0 + '$' + r1;
        }

      // 小数の場合
      } else {
        const i1 = this.$_x10000(s1);
        const i2 = this.$_x10000(s2);
        let ret = 0;
        if      (operation === '+') ret = i1 + i2;
        else if (operation === '-') ret = i1 - i2;
        else if (operation === '*') ret = i1 * i2 / 10000;
        else if (operation === '/') ret = i1 * 10000 / i2;
        return this.$_d10000(ret);
      }

      return '';
    },
    $_x10000(str: string): number {
      const pos = str.indexOf('.');
      if (pos === -1) return Number(str) * 10000;

      // 整数部
      const str1 = str.substring(0, pos);
      // 小数部
      let   str2 = str.substring(pos + 1);
      while (str2.length < 4) str2 += '0'; // 4桁にする

      return Number(str1) * 10000 + Number(str2);
    },
    $_d10000(num: number): string {
      const i1 = Math.floor(num / 10000);
      const i2 = num % 10000;
      if (i2 === 0) return String(i1);

      // 整数部
      const str1 = String(i1);
      // 小数部
      let   str2 = String(i2);
      while (str2.length < 4) str2 = '0' + str2; // 4桁にする

      // 小数部の後ろの0を省く
      for (let i = 3; i > 0; i--) {
        if (str2.charAt(i) !== '0') break;
        str2 = str2.substring(0, i);
      }

      return str1 + '.' + str2;
    },
    $_isLong(right: string): boolean {
      // 解答が複数ある場合は長文字列ではない
      if (right.indexOf(';') !== -1) return false;
      if (right.indexOf('$') !== -1) return false;

      // 分数が含まれる場合は長文字列ではない
      if (right.indexOf('[') !== -1) return false;

      // 入力欄の幅をとらない文字を省く
      right = right.replace(/\^/g, ''); // 累乗
      right = right.replace(/ /g,  ''); // ルートの終わり
      right = right.replace(/&/g,  ''); // 因数分解

      // 別解がある場合は一番前のものを使く
      if (right.indexOf('|') !== -1) {
        const rights = right.split('|');
        right = rights[0];
      }

      // 解答が10文字を超える場合は長文字列にする
      return right.length > 10;
    },
    $_getConfirmPosObject(x: number, y: number): Object {
      return {
        left: x + 'px',
        top:  y + 'px'
      };
    }
  }

});
